import { InitCardStudioDevToolsArgs } from './DevToolsContext'

// All default should be OFF if you aren't checking devToolsEnabled!
// Production users could get these options!
export const devToolsEnabled = process.env['NODE_ENV'] === 'development' || process.env['NODE_ENV'] === 'test'

const STORAGE_KEY = 'devtools-settings'

const getStoredSettings = () => {
  if (typeof window === 'undefined') return null
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

const storedSettings = getStoredSettings()

export const DEFAULT_DEVTOOLS_SETTINGS = {
  cardStudioTools: storedSettings?.cardStudioTools ?? false,
  reactQueryTools: storedSettings?.reactQueryTools ?? false,
  reactReduxTools: storedSettings?.reactReduxTools ?? devToolsEnabled,
  echoTools: storedSettings?.echoTools ?? false,
  echoDebugMode: storedSettings?.echoDebugMode ?? false,
  echoServerAddress: storedSettings?.echoServerAddress ?? '',
}
export const INITIAL_CARD_STUDIO_STATE = {
  context: null as CanvasRenderingContext2D | null,
  metrics: {} as InitCardStudioDevToolsArgs['metrics'],
  imageData: '',
  showGrid: false,
  showCoordinates: false,
  strokeOptions: {},
  shouldCreatePreview: false,
}

export type DevToolsSettings = typeof DEFAULT_DEVTOOLS_SETTINGS
export type CardStudioState = typeof INITIAL_CARD_STUDIO_STATE
export type ToolKey = {
  [Key in keyof DevToolsSettings]: DevToolsSettings[Key] extends boolean ? Key : never
}[keyof DevToolsSettings]

type ToggleAction = { type: 'TOGGLE'; key: ToolKey }
type SetEchoServerAddressAction = { type: 'SET_ECHO_SERVER_ADDRESS'; address: string }
type UpdateImageData = { type: 'UPDATE_IMAGE_DATA'; imageData: string }
type ToggleCardStudioOption = { type: 'TOGGLE_CARD_STUDIO_OPTION'; key: 'showGrid' | 'showCoordinates' }
type TogglePreviewAction = { type: 'TOGGLE_PREVIEW'; value: boolean }
type SetContextAction = { type: 'SET_CONTEXT'; context: CanvasRenderingContext2D }
type SetMetricsAction = {
  type: 'SET_METRICS'
  metrics: InitCardStudioDevToolsArgs['metrics']
}
type SetStrokeOptionsAction = {
  type: 'SET_STROKE_OPTIONS'
  strokeOptions: InitCardStudioDevToolsArgs['strokeOptions']
}

const storeSettings = (settings: typeof DEFAULT_DEVTOOLS_SETTINGS) => {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
    } catch {
      // Ignore localStorage errors
    }
  }
}

export function enabledReducer(
  state: typeof DEFAULT_DEVTOOLS_SETTINGS,
  action: ToggleAction | SetEchoServerAddressAction
) {
  switch (action.type) {
    case 'TOGGLE': {
      const newState = { ...state, [action.key]: !state[action.key] }
      storeSettings(newState)
      return newState
    }
    case 'SET_ECHO_SERVER_ADDRESS': {
      const newState = { ...state, echoServerAddress: action.address }
      storeSettings(newState)
      return newState
    }
    default:
      return state
  }
}

export function cardStudioReducer(
  state: typeof INITIAL_CARD_STUDIO_STATE,
  action:
    | UpdateImageData
    | ToggleCardStudioOption
    | SetContextAction
    | SetMetricsAction
    | SetStrokeOptionsAction
    | TogglePreviewAction
) {
  switch (action.type) {
    case 'SET_CONTEXT':
      return { ...state, context: action.context }
    case 'SET_METRICS':
      return { ...state, metrics: action.metrics }
    case 'UPDATE_IMAGE_DATA':
      return { ...state, imageData: action.imageData }
    case 'TOGGLE_CARD_STUDIO_OPTION':
      return { ...state, [action.key]: !state[action.key] }
    case 'SET_STROKE_OPTIONS':
      return { ...state, strokeOptions: action.strokeOptions }
    case 'TOGGLE_PREVIEW':
      return { ...state, shouldCreatePreview: action.value }
    default:
      return state
  }
}
